import React, { useState } from 'react';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

import {
  Assessment,
  AssessmentsAPI,
  ResetParams,
} from '@/services/assessmentsAPI';
import Modal from '@/components/Modal';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import Button from '@/components/Button';
import Alert from '@/components/Alert';
import { Body, Small } from '@/components/Typography';

interface AdminResetDialogProps {
  assessment: Assessment;
  onSuccess: () => void;
  onCancel: () => void;
}

const AdminResetDialog: React.FC<AdminResetDialogProps> = ({
  assessment,
  onSuccess,
  onCancel,
}) => {
  const [resetType, setResetType] = useState<'full' | 'retry_only'>('full');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const resetData: ResetParams = { reset_type: resetType };
      if (notes.trim()) {
        resetData.notes = notes.trim();
      }
      await AssessmentsAPI.resetAssessment(assessment.external_id, resetData);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.errors?.[0] || 'Failed to reset lockout');
      console.error('[AdminResetDialog] Error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      displayFlex
      size="sm"
      title="Reset Worker Lockout"
      onRequestClose={onCancel}
      wrapperBackground={true}
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={16}>
            <div
              style={{
                backgroundColor: '#FFF4E6',
                padding: '12px 16px',
                borderRadius: '6px',
                borderLeft: '3px solid #DEA83E',
              }}
            >
              <Stack vertical gap={4}>
                <Small weight="semibold">Current Lockout Status</Small>
                <Small>
                  Reason:{' '}
                  {assessment.lock_reason === 'minimal_attempts'
                    ? 'Too many minimal attempts'
                    : assessment.lock_reason || 'Unknown'}
                </Small>
                <Small>Locked since: {formatDate(assessment.locked_at)}</Small>
                <Small>Dial count: {assessment.dial_count ?? 0}</Small>
                <Small>
                  Minimal attempts: {assessment.minimal_attempt_count ?? 0}
                </Small>
                <Small>
                  Auto-reset: {formatDate(assessment.auto_reset_eligible_at)}
                </Small>
              </Stack>
            </div>

            {error && (
              <Alert
                status="danger"
                icon={faExclamationTriangle}
                description={error}
              />
            )}

            <Stack vertical gap={4}>
              <Body weight="medium">Reset Type</Body>
              <select
                value={resetType}
                onChange={(e) =>
                  setResetType(e.target.value as 'full' | 'retry_only')
                }
                disabled={submitting}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                }}
              >
                <option value="full">Full Reset (clear lock + counters)</option>
                <option value="retry_only">
                  Retry Only (clear lock, keep counters)
                </option>
              </select>
            </Stack>

            <Stack vertical gap={4}>
              <Body weight="medium">Notes</Body>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Reason for reset..."
                disabled={submitting}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                }}
              />
            </Stack>

            <Stack justify="end" gap={12}>
              <Button
                a11yLabel="Cancel reset"
                appearance="outline"
                label="Cancel"
                onClick={onCancel}
                disabled={submitting}
              />
              <Button
                a11yLabel="Confirm reset"
                appearance="primary"
                label={submitting ? 'Resetting...' : 'Reset Lockout'}
                onClick={handleSubmit}
                disabled={submitting}
              />
            </Stack>
          </Stack>
        </Card.Section>
      </Card>
    </Modal>
  );
};

export default AdminResetDialog;
